/* ════ IMPORT MS PLANNER EXPORT (xlsx) ═════════════════════ */
// Reads the "Consolidated Data" sheet of a Planner → Excel export.
//   Task Name → title (Client / job number parsed out)
//   Bucket    → consultant
//   Assigned To → resource (bulk choice; default = the file's value)
//   Start date → start,  Due date → end
//   Checklist Items → subtasks,  Labels → tags,  Notes → notes
import { state } from './state.js';
import { $ } from './dom.js';
import { RES_COLS } from './config.js';
import { sb } from './supabase/client.js';
import { setSyncDot } from './supabase/data.js';
import { showToast } from './utils/toast.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const norm = s => String(s || '').trim().toLowerCase();
let parsed = null, _xlsx = null;
async function getXLSX() { if (!_xlsx) { const m = await import('xlsx'); _xlsx = m.read ? m : m.default; } return _xlsx; }

function iso(v) {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return null;
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const mapImp = p => { const v = norm(p); return (v.includes('urgent') || v.includes('important') || v.includes('high')) ? 'high' : v.includes('low') ? 'low' : 'normal'; };
const splitChecklist = v => String(v || '').split(/[;\n]+/).map(s => s.trim()).filter(Boolean).map(text => ({ text, done: false }));
const splitList = v => String(v || '').split(/[;,]+/).map(s => s.trim()).filter(Boolean);

// "AZ - Oncology RD Strategy CI - red10-26-007" → {client:AZ, job:red10-26-007, name:"Oncology RD Strategy CI"}
// Pulls the red10-NN-NNN reference from anywhere in the title; client = first segment.
function parseTitle(raw) {
  let s = String(raw || '').trim();
  let job = '';
  const m = s.match(/red10[-\s]?\d{1,2}[-\s]?\d{1,4}\w*/i);
  if (m) { job = m[0].trim(); s = s.replace(m[0], ' '); }
  const parts = s.split(' - ').map(p => p.trim().replace(/^[-:\s]+|[-:\s]+$/g, '').trim()).filter(Boolean);
  let client = '', name = parts.join(' - ');
  if (parts.length >= 2) { client = parts[0]; name = parts.slice(1).join(' - '); }
  name = name.replace(/[-:\s]+$/, '').trim();
  return { client, job, name: name || raw };
}

async function parseFile(file) {
  const XLSX = await getXLSX();
  const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
  const pick = names => names.find(n => wb.Sheets[n]) || wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[pick(['Consolidated Data', 'Tasks'])], { header: 1, blankrows: false, cellDates: true });
  const planName = wb.Sheets['Plan'] ? (XLSX.utils.sheet_to_json(wb.Sheets['Plan'], { header: 1 })[1]?.[1] || 'Planner') : 'Planner';
  const H = (rows[0] || []).map(x => norm(x));
  const col = (...names) => {
    for (const n of names) { const i = H.indexOf(norm(n)); if (i >= 0) return i; }
    for (const n of names) { const i = H.findIndex(h => h.includes(norm(n))); if (i >= 0) return i; }
    return -1;
  };
  const c = {
    name: col('task name', 'name', 'title'), bucket: col('bucket'), priority: col('priority'),
    assigned: col('assigned to', 'assigned'), due: col('due date', 'due'), start: col('start date', 'start'),
    checklist: col('checklist items', 'checklist'), labels: col('labels', 'label'), notes: col('notes', 'description'),
  };
  const tasks = [];
  for (const r of rows.slice(1)) {
    const rawName = c.name >= 0 ? String(r[c.name] || '').trim() : '';
    if (!rawName) continue;
    const { client, job, name } = parseTitle(rawName);
    tasks.push({
      rawName, client, job, name,
      consultant: c.bucket >= 0 ? String(r[c.bucket] || '').trim() : '',
      assignee: c.assigned >= 0 ? String(r[c.assigned] || '').trim() : '',
      start: c.start >= 0 ? iso(r[c.start]) : null,
      due: c.due >= 0 ? iso(r[c.due]) : null,
      priority: c.priority >= 0 ? String(r[c.priority] || '') : '',
      checklist: c.checklist >= 0 ? splitChecklist(r[c.checklist]) : [],
      labels: c.labels >= 0 ? splitList(r[c.labels]) : [],
      notes: c.notes >= 0 ? String(r[c.notes] || '').trim() : '',
    });
  }
  parsed = { planName, tasks };
}

// Resolve an assignee name to a resource id (match by first name), creating a lane if needed.
async function resolveRes(name, cache) {
  name = (name || '').trim();
  if (!name) return 'unassigned';
  if (cache[name]) return cache[name];
  const first = norm(name).split(' ')[0];
  const ex = state.resources.find(r => { const rn = norm(r.name); return rn === norm(name) || rn === first || rn.split(' ')[0] === first; });
  if (ex) { cache[name] = ex.id; return ex.id; }
  const id = name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now() + '-' + Math.floor(Math.random() * 1e4);
  const col = RES_COLS[state.resources.length % RES_COLS.length];
  const ini = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  const row = { id, name, ini, col, sort: state.resources.length };
  await sb.from('resources').insert(row);
  state.resources.push(row); cache[name] = id; return id;
}

function openModal() {
  $('imp-count').textContent = `${parsed.tasks.length} tasks from “${parsed.planName}”`;
  $('imp-assign').innerHTML =
    `<option value="assignee">Use “Assigned To” from file</option>` +
    state.resources.filter(r => r.id !== 'unassigned').map(r => `<option value="${r.id}">${esc(r.name)}</option>`).join('') +
    `<option value="unassigned">Unassigned</option>`;
  $('imp-preview').innerHTML = parsed.tasks.map((t, i) => {
    const tmpl = norm(t.consultant) === 'template' || /\b(client|team|ref)\b.*\b(team|ref)\b/i.test(t.rawName) && !t.start && !t.due;
    return `<label class="imp-row${tmpl ? ' off' : ''}">
      <input type="checkbox" class="imp-chk" data-i="${i}"${tmpl ? '' : ' checked'}>
      <span class="imp-rcl">${esc(t.client || '')}</span>
      <span class="imp-rn">${esc(t.name || t.rawName)}</span>
      <span class="imp-rc">${esc(t.consultant || '')}</span>
      <span class="imp-rd">${(t.start || t.due) ? (t.start || '') + (t.due && t.due !== t.start ? ' → ' + t.due : '') : '<span class="imp-inbox">→ Inbox (no date)</span>'}</span>
    </label>`;
  }).join('');
  $('import-modal').classList.add('open');
}

async function doImport() {
  if (!parsed) return;
  const target = $('imp-target').value, assign = $('imp-assign').value, cat = $('imp-cat').value, me = state.currentUser?.email || null;
  setSyncDot('busy');
  const cache = {};
  const resFor = async t => assign === 'unassigned' ? 'unassigned' : assign === 'assignee' ? await resolveRes(t.assignee, cache) : assign;
  const included = new Set([...$('imp-preview').querySelectorAll('.imp-chk:checked')].map(c => +c.dataset.i));
  const taskRows = [], ideaRows = [];
  for (const [i, t] of parsed.tasks.entries()) {
    if (!included.has(i)) continue;
    const resId = await resFor(t);
    if (target === 'planner' && (t.start || t.due)) {
      let s = t.start || t.due, e = t.due || t.start; if (e < s) e = s;
      taskRows.push({
        name: t.name || t.rawName, client: t.client || '', team: '', job: t.job || '',
        start: s, end: e, half: 'none', cat, importance: mapImp(t.priority),
        resource: resId, consultant: t.consultant || '', info: t.notes || '',
        tags: t.labels, subtasks: t.checklist, hours: null, locked: false, link: '', created_by: me,
      });
    } else {
      ideaRows.push({
        name: t.name || t.rawName, description: t.notes || '', cat, importance: mapImp(t.priority),
        resource: resId === 'unassigned' ? null : resId, days: null, due: t.due || null,
        tags: t.labels, subtasks: t.checklist, created_by: me,
      });
    }
  }
  let err = null;
  for (let i = 0; i < taskRows.length && !err; i += 100) ({ error: err } = await sb.from('tasks').insert(taskRows.slice(i, i + 100)));
  for (let i = 0; i < ideaRows.length && !err; i += 100) ({ error: err } = await sb.from('ideas').insert(ideaRows.slice(i, i + 100)));
  if (err) { setSyncDot('err'); showToast('Import failed: ' + err.message); return; }
  setSyncDot('ok'); $('import-modal').classList.remove('open');
  showToast(`Imported ${taskRows.length} to planner${ideaRows.length ? ', ' + ideaRows.length + ' to inbox' : ''} ✓`);
}

export function initImport() {
  const inp = $('import-file');
  $('btn-import').addEventListener('click', () => inp.click());
  inp.addEventListener('change', async e => {
    const file = e.target.files[0]; if (!file) return;
    setSyncDot('busy');
    try { await parseFile(file); setSyncDot('ok'); openModal(); }
    catch (err) { setSyncDot('err'); showToast('Could not read file: ' + err.message); }
    inp.value = '';
  });
  $('imp-x').addEventListener('click', () => $('import-modal').classList.remove('open'));
  $('imp-cancel').addEventListener('click', () => $('import-modal').classList.remove('open'));
  $('imp-run').addEventListener('click', doImport);
  $('imp-all').addEventListener('change', e => $('imp-preview').querySelectorAll('.imp-chk').forEach(c => {
    c.checked = e.target.checked; c.closest('.imp-row').classList.toggle('off', !e.target.checked);
  }));
  $('imp-preview').addEventListener('change', e => {
    if (e.target.classList.contains('imp-chk')) e.target.closest('.imp-row').classList.toggle('off', !e.target.checked);
  });
}
